import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// SESLİ YAZDIRMA (dikte) — Web Speech API sarmalayıcısı, tr-TR.
//
// NEDEN HOOK: mikrofon yaşam döngüsü (izin, ara sonuç, hata, otomatik yeniden
// başlatma, soru değişiminde durdurma) baştan sona yan-etki yönetimidir;
// SoruKarti zaten 900 satır. Buton bileşeni yalnız "dinliyor mu" bilgisini
// alır, tanıma nesnesini hiç görmez.
//
// ⛔ TARAYICI: yalnız Chrome/Edge (webkitSpeechRecognition). Firefox/Safari'de
//    yok — buton gizlenmez, PASİFLEŞİR (sessiz ölü buton olmasın).
// ⛔ GÜVENLİ BAĞLAM: Chrome, HTTP origin'de de yapıcıyı TANIMLI bırakır ama
//    start() anında `not-allowed` verir. Bu yüzden `isSecureContext` AYRICA
//    kontrol edilir — yoksa öğretmen "izin reddedildi" görüp Chrome ayarlarında
//    boşuna izin arar. localhost güvenli sayılır → `npm start` ile özellik
//    HTTPS geçişini beklemeden test edilebilir.
// ⚠️ GİZLİLİK: Chrome sesi Google sunucusuna gönderir; çevrimdışı çalışmaz.
// ─────────────────────────────────────────────────────────────────────────────

function tanimaYapicisi() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/* ── SAF YARDIMCILAR (DOM'suz, test edilebilir) ───────────────────────────── */

// Cümle başına yapışan noktalama: tanıma ", ayrıca" gibi bir parça döndürürse
// önüne boşluk KOYULMAZ.
const NOKTALAMA_BAS = /^[,.;:!?…)\]}»]/;

/**
 * Dikte parçasını mevcut yoruma ekler.
 * KURAL: SONA ekler, imleç konumuna DEĞİL. Gerekçe: textarea controlled;
 * value değişince caret sona kaçar, imleç ekleme selectionStart takibi +
 * setSelectionRange ile caret geri yükleme ister ve dikte 1-2 sn'de bir parça
 * gönderdiği için caret sürekli zıplar.
 * Harf BÜYÜTÜLMEZ: JS toUpperCase() Türkçe 'i'yi 'I' yapar ('İ' değil) ve
 * tr-TR tanıma zaten cümle başını büyük döndürür.
 */
export function metinBirlestir(mevcut, yeni) {
  const parca = (yeni || '').trim();
  const eski = mevcut || '';
  if (!parca) return eski;                            // boş parça hiçbir şeyi bozmaz
  if (!eski.trim()) return parca;                     // boş kutu: baştan yaz
  if (/\s$/.test(eski)) return eski + parca;          // boşluk/satır sonu zaten var
  if (NOKTALAMA_BAS.test(parca)) return eski + parca; // ", ayrıca" → boşluksuz
  return eski + ' ' + parca;
}

/**
 * SpeechRecognitionErrorEvent.error → { metin, olumcul } | null
 * `olumcul: true`  ⇒ otomatik yeniden başlatma YAPILMAZ (sonsuz döngü kilidi).
 * `null`           ⇒ kullanıcıya hiçbir şey gösterilmez (bizim iptalimiz).
 */
export function sesHataMesaji(kod) {
  switch (kod) {
    case 'not-allowed':
      return { metin: 'Mikrofon izni verilmedi. Adres çubuğundaki kilit simgesinden bu siteye mikrofon izni verip tekrar deneyin.', olumcul: true };
    case 'service-not-allowed':
      return { metin: 'Tarayıcı ses tanıma servisini engelledi. Site ayarlarından mikrofon iznini kontrol edin.', olumcul: true };
    case 'audio-capture':
      return { metin: 'Mikrofon bulunamadı. Cihaz takılı ve ses ayarlarında seçili mi?', olumcul: true };
    case 'network':
      return { metin: 'Ses tanıma sunucusuna ulaşılamadı — internet bağlantısı gerekiyor.', olumcul: true };
    case 'no-speech':
      // ÖLÜMCÜL DEĞİL: öğretmen düşünürken susar, dinleme sürmeli.
      return { metin: 'Ses algılanmadı — dinlemeye devam ediliyor.', olumcul: false };
    case 'aborted':
      return null;   // soru değişimi / unmount / başka karta geçiş — bizim iptalimiz
    default:
      return { metin: `Sesli yazdırma hatası (${kod || 'bilinmeyen'}).`, olumcul: true };
  }
}

/* ── TEK AKTİF DİKTE (grup görünümü koruması) ─────────────────────────────── */
// BaglamGrupKarti sayfada uyeler.map ile N adet SoruKarti basar → N mikrofon
// butonu. İkinci start() mikrofonu çalar ve birinci kart SESSİZCE ölür:
// öğretmen konuşmaya devam eder, metin hiçbir yere yazılmaz. Modül düzeyindeki
// bu kayıt, yeni dikte başlarken öncekini kapatır.
let aktifDikte = null;   // { kimlik, iptal }

/* ── HOOK ─────────────────────────────────────────────────────────────────── */

export default function useSesliYazdirma({ anahtar, onKesinMetin, dil = 'tr-TR' }) {
  const Yapici = tanimaYapicisi();
  const guvenli = typeof window === 'undefined' || window.isSecureContext !== false;
  const destekli = !!Yapici && guvenli;
  const destekMesaji = !Yapici
    ? 'Bu tarayıcı sesli yazdırmayı desteklemiyor — Chrome veya Edge kullanın.'
    : !guvenli
      ? 'Sesli yazdırma yalnız güvenli bağlantıda (HTTPS) çalışır.'
      : null;

  const [dinliyor, setDinliyor] = useState(false);
  const [araMetin, setAraMetin] = useState('');
  const [mesaj, setMesaj] = useState(null);   // { metin, tip: 'hata' | 'bilgi' }

  const tanimaRef = useRef(null);
  const kimlikRef = useRef({});          // bu hook örneğinin stabil kimliği
  const kullaniciDurdu = useRef(false);
  const olumculHata = useRef(false);
  const kisaOturumSayac = useRef(0);
  const oturumBasi = useRef(0);
  const sonKesinIndex = useRef(-1);

  // (b) BAYAT CLOSURE: onresult işleyicisi bir kez kurulur ve ömrü boyunca
  // kurulum anındaki callback'i tutar. Ref her render'da tazelenir.
  const onKesinRef = useRef(onKesinMetin);
  useEffect(() => { onKesinRef.current = onKesinMetin; });

  /**
   * (d)(e) SÖK: önce işleyicileri KOPAR, sonra abort().
   * Koparmazsak abort() → onend → otomatik yeniden başlatma zinciri tetiklenir
   * ve soru değiştikten/kart söküldükten sonra mikrofon AÇIK kalır.
   * abort() bekleyen ara sonucu ATAR — stop() olsaydı eski sorunun son cümlesi
   * yeni sorunun kutusuna düşerdi.
   */
  const iptalEt = useCallback(() => {
    kullaniciDurdu.current = true;
    const t = tanimaRef.current;
    tanimaRef.current = null;
    if (t) {
      t.onstart = null; t.onresult = null; t.onerror = null; t.onend = null;
      try { t.abort(); } catch (_) { /* zaten kapalı */ }
    }
    if (aktifDikte && aktifDikte.kimlik === kimlikRef.current) aktifDikte = null;
    setDinliyor(false);
    setAraMetin('');
  }, []);

  const basla = useCallback(() => {
    if (!destekli || tanimaRef.current) return;
    // Sayfadaki başka bir kartta dikte açıksa önce onu kapat (tek aktif kilidi).
    if (aktifDikte && aktifDikte.kimlik !== kimlikRef.current) aktifDikte.iptal();

    const t = new Yapici();
    t.lang = dil;
    t.continuous = true;      // (c) yine de sessizlikte / ~60 sn'de biter → onend
    t.interimResults = true;  // gri canlı önizleme
    t.maxAlternatives = 1;

    const bitir = () => {
      tanimaRef.current = null;
      if (aktifDikte && aktifDikte.kimlik === kimlikRef.current) aktifDikte = null;
      setDinliyor(false);
      setAraMetin('');
    };

    t.onstart = () => {
      // Her oturum results dizinini SIFIRDAN başlatır; otomatik yeniden
      // başlatmadan sonra eski indeksle karşılaştırmak yeni oturumun metnini
      // TÜMÜYLE yutar.
      sonKesinIndex.current = -1;
      oturumBasi.current = Date.now();
      setDinliyor(true);
      setMesaj(null);
    };

    t.onresult = (olay) => {
      // (a) `results` KÜMÜLATİFTİR. resultIndex'ten başlamazsak her olayda tüm
      // oturumun metnini yeniden ekleriz ("Merhaba Merhaba Merhaba").
      // sonKesinIndex ek sigortadır: aynı indeks iki kez kesinleşirse yutar.
      let kesin = '';
      let ara = '';
      for (let i = olay.resultIndex; i < olay.results.length; i++) {
        const parca = olay.results[i][0].transcript;
        if (olay.results[i].isFinal) {
          if (i > sonKesinIndex.current) { kesin += parca; sonKesinIndex.current = i; }
        } else {
          ara += parca;   // ara sonuç BİRİKTİRİLMEZ, her olayda yeniden kurulur
        }
      }
      setAraMetin(ara);
      if (kesin) {
        kisaOturumSayac.current = 0;
        setMesaj(null);                     // 'no-speech' uyarısını temizle
        if (onKesinRef.current) onKesinRef.current(kesin);
      }
    };

    t.onerror = (olay) => {
      const h = sesHataMesaji(olay && olay.error);
      if (!h) return;                       // 'aborted' → sessiz
      olumculHata.current = h.olumcul;      // (c)(f) izin reddi → restart YOK
      setMesaj({ metin: h.metin, tip: h.olumcul ? 'hata' : 'bilgi' });
    };

    t.onend = () => {
      setAraMetin('');
      if (kullaniciDurdu.current || olumculHata.current) { bitir(); return; }
      // (c) SONSUZ DÖNGÜ KORUMASI — zamana dayalı:
      // Sessizlikten biten oturum UZUNDUR (>1 sn) → sayaç sıfırlanır, öğretmen
      // 5 dakika düşünse bile dikte açık kalır. İzin kutusu yanıtsız / cihaz yok
      // durumunda oturum ANINDA biter → 3 kısa oturumda durur ve mikrofon izin
      // kutusu tekrar tekrar açılmaz.
      if (Date.now() - oturumBasi.current < 1000) kisaOturumSayac.current += 1;
      else kisaOturumSayac.current = 0;
      if (kisaOturumSayac.current >= 3) {
        bitir();
        setMesaj({ metin: 'Mikrofon dinlemeye devam edemedi — butona tekrar basın.', tip: 'hata' });
        return;
      }
      try { t.start(); } catch (_) { bitir(); }
    };

    tanimaRef.current = t;
    kullaniciDurdu.current = false;
    olumculHata.current = false;
    kisaOturumSayac.current = 0;
    aktifDikte = { kimlik: kimlikRef.current, iptal: iptalEt };
    try {
      t.start();
    } catch (_) {
      bitir();
      setMesaj({ metin: 'Mikrofon başlatılamadı.', tip: 'hata' });
    }
  }, [destekli, dil, Yapici, iptalEt]);

  /**
   * Kullanıcının durdurması: stop() bekleyen ara sonucu KESİNLEŞTİRİP bitirir
   * → son cümle kaybolmaz. (abort() atardı; o yalnız soru değişimi/unmount.)
   */
  const durdur = useCallback(() => {
    kullaniciDurdu.current = true;
    const t = tanimaRef.current;
    if (!t) { setDinliyor(false); return; }
    try { t.stop(); } catch (_) { iptalEt(); }
  }, [iptalEt]);

  const degistir = useCallback(() => {
    if (dinliyor) durdur(); else basla();
  }, [dinliyor, durdur, basla]);

  // (d) SORU DEĞİŞİNCE DUR + (e) UNMOUNT'TA ABORT — tek effect, gövdesi yok.
  // `anahtar` = soru.id. VERİ BÜTÜNLÜĞÜ: bu olmazsa 1. sorunun dikte cümlesi
  // 2. sorunun yorum kutusuna düşer ve öğretmen fark etmeden kaydedebilir.
  // Kural HOOK'un içinde durur, çağıranın hatırlamasına bırakılmaz.
  // React bir commit'te önce TÜM temizleyicileri, sonra effect gövdelerini
  // koşar → mikrofon, SoruKarti'nin setYorum(...) sıfırlamasından ÖNCE kapanır.
  // Tanıma nesnesi effect gövdesinde değil KULLANICI TIKLAMASIYLA kurulduğu
  // için StrictMode çift-mount'ta boşa nesne/izin isteği oluşmaz.
  useEffect(() => () => { iptalEt(); }, [anahtar, iptalEt]);

  return { destekli, destekMesaji, dinliyor, araMetin, mesaj, basla, durdur, degistir };
}
